import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { PageSpinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/utils";
import { ClipboardCheck, Camera, Receipt, FileText, DollarSign, Phone, Activity, ArrowRight } from "lucide-react";

interface TimelineItem {
    id: string;
    type: string;
    title: string;
    detail?: string;
    actor?: string;
    timestamp: string;
    meta?: Record<string, unknown>;
}

const TYPE_CONFIG: Record<string, { icon: typeof Activity; color: string }> = {
    audit: { icon: Activity, color: "text-gray-400" },
    inspection: { icon: ClipboardCheck, color: "text-blue-500" },
    photo: { icon: Camera, color: "text-purple-500" },
    billing: { icon: Receipt, color: "text-amber-500" },
    estimate: { icon: FileText, color: "text-indigo-500" },
    invoice: { icon: DollarSign, color: "text-green-500" },
    call: { icon: Phone, color: "text-teal-500" },
};

interface ActivityTimelineProps {
    jobId?: string;
    entityType?: string;
    entityId?: string;
    limit?: number;
    /**
     * When provided, render these items directly and skip the entity-scoped
     * query. Used by actor-scoped feeds (e.g. the admin Employee Report) that
     * fetch their own timeline but want identical rendering.
     */
    items?: TimelineItem[];
}

export function ActivityTimeline({ jobId, entityType, entityId, limit, items: itemsProp }: ActivityTimelineProps) {
    const resolvedJobId = jobId || (entityType === "job" ? entityId : undefined);

    const resolvedEntityType = resolvedJobId ? "job" : entityType;
    const resolvedEntityId = resolvedJobId ?? entityId;
    const resolvedLimit = limit ?? 50;

    const { data, isLoading } = useQuery({
        queryKey: ["activity-timeline", resolvedEntityType ?? "none", resolvedEntityId ?? "none", resolvedLimit],
        queryFn: () => {
            if (!resolvedEntityType || !resolvedEntityId) {
                return Promise.resolve({ timeline: [] as TimelineItem[] });
            }
            return api.get<{ timeline: TimelineItem[] }>(
                `/api/activity?entityType=${encodeURIComponent(resolvedEntityType)}&entityId=${encodeURIComponent(resolvedEntityId)}&limit=${resolvedLimit}`,
            );
        },
        enabled: !itemsProp && !!(resolvedEntityType && resolvedEntityId),
    });

    if (!itemsProp && isLoading) return <PageSpinner />;

    const items = itemsProp ?? data?.timeline ?? [];

    if (items.length === 0) {
        return (
            <div className="text-center py-8 text-shell-foreground/70 text-sm">
                <Activity className="mx-auto h-8 w-8 opacity-40 mb-2" />
                No activity yet
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-0">
                {items.map((item) => {
                    const config = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.audit;
                    const Icon = config.icon;
                    return (
                        <div key={item.id} className="relative flex gap-3 py-2.5 pl-0">
                            <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-card">
                                <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium">{item.title}</span>
                                    <Badge variant="outline" className="text-[10px] capitalize">
                                        {item.type}
                                    </Badge>
                                </div>
                                {item.detail && <p className="text-xs text-foreground/80 mt-0.5">{item.detail}</p>}
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-foreground/80">
                                    <span>{formatDateTime(item.timestamp)}</span>
                                    {item.actor && (
                                        <>
                                            <ArrowRight className="h-2.5 w-2.5" />
                                            <span>{item.actor}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
